import { useMemo } from 'react';
import quaRaw from '../data/qua.json';
import BrandCatalog from '../components/catalog/BrandCatalog';
import type { CatalogItem, BrandConfig } from '../components/catalog/BrandCatalog';
import { useSEO, localKeywords } from '@/hooks/useSEO';
import { useStructuredData } from '@/hooks/useStructuredData';
import { buildBrandCatalogSchema, buildBreadcrumbSchema } from '@/lib/seo/schemas';

interface QuaRaw {
  id: string;
  title: string;
  collection: string;
  category: string;
  sku: string;
  images: string[];
  description: string;
  specs: Record<string, string>;
  features: string[];
  tags: string[];
}

/* ══════════════════════════════════════════════════════════════
   QUA KATEGORİ TAKSONOMİSİ
   ══════════════════════════════════════════════════════════════ */

// Ebat → grup
const SIZE_GROUPS: Record<string, string> = {
  '120X120': 'Büyük Format (120x120+)',
  '100X100': 'Büyük Format (120x120+)',
  '80X80':   'Geniş Format (80x80)',
  '60X120':  'Dikdörtgen (60x120)',
  '40X120':  'Uzun (40x120+)',
  '30X120':  'Uzun (40x120+)',
  '20X120':  'Ahşap Görünüm (20x120)',
  '60X90':   'Geniş Format (60x90)',
  '60X60':   'Kare (60x60)',
  '61X61':   'Kare (60x60)',
  '30X90':   'Dikdörtgen (30x90)',
  '30X60':   'Standart (30x60)',
  '15X60':   'Çubuk (15x60)',
};

function sizeGroup(size: string): string {
  const norm = size.replace(/\s/g, '').toUpperCase();
  return SIZE_GROUPS[norm] || `${norm} cm`;
}

// Doku tespiti
function detectTexture(title: string, tags: string[]): string {
  const t = title.toLowerCase();
  const joined = tags.join(' ').toLowerCase();
  if ([t, joined].some(s => /(travertino|travertine)/.test(s))) return 'Travertin';
  if ([t, joined].some(s => /(mermer|marble|calacatta|dolomiti|bianco|cippolino)/.test(s))) return 'Mermer';
  if ([t, joined].some(s => /(ahşap|ahsap|teak|walnut|oak|maple|honey|chesnut|venge|alder|faun|diamond|chelsea|altar|amazonica|argon|capri)/.test(s))) return 'Ahşap';
  if ([t, joined].some(s => /(beton|concrete|ark|benelux|chalky|epoch|loft|urban)/.test(s))) return 'Beton';
  if ([t, joined].some(s => /(taş|stone|cora|canyon|slate)/.test(s))) return 'Taş';
  return 'Porselen';
}

function mapToItem(p: QuaRaw): CatalogItem {
  const size = p.specs['Ebat'] || '';
  const sizeNorm = size.replace(' cm', '').replace(/\s/g, '').toUpperCase();
  const grp = sizeGroup(sizeNorm);
  const texture = detectTexture(p.title, p.tags);

  return {
    id: p.id,
    title: p.title,
    sku: p.sku,
    images: p.images,
    groupKey: p.category,
    groupLabel: p.category,
    subtitle: p.collection !== p.title ? p.collection : undefined,
    tags: p.tags,
    chipFilters: {
      'Kategori':  p.category,
      'Koleksiyon': p.collection,
      'Ebat':       grp,
      'Doku':       texture,
      'Yüzey':      p.specs['Yüzey'] || 'Mat',
    },
    specs: p.specs,
    features: p.features,
    description: p.description,
  };
}

const quaBrand: BrandConfig = {
  brandName: 'QUA Granite',
  tagline: 'Porselen Karolar | 430+ Ürün | Mutfak · Banyo · Bahçe · Dış Cephe',
  accentColor: '#795548',
  accentLight: '#efebe9',
  accentMid: '#d7ccc8',
  groupMode: 'flat',
  searchPlaceholder: 'Koleksiyon veya ebat ara… (örn: Ark, 60x120, mermer)',
  filterGroups: [
    { label: 'Kategori',    field: 'Kategori' },
    { label: 'Koleksiyon',  field: 'Koleksiyon' },
    { label: 'Doku',        field: 'Doku' },
    { label: 'Ebat',        field: 'Ebat' },
    { label: 'Yüzey',       field: 'Yüzey' },
  ],
};

export default function Qua() {
  const items = useMemo<CatalogItem[]>(
    () => (quaRaw as unknown as QuaRaw[]).map(mapToItem),
    []
  );

  useSEO({
    title: 'QUA Granite Diyarbakır | Porselen & Granit Karo Kataloğu — Mayi Mimarlık',
    description:
      "Diyarbakır QUA Granite ana bayi Mayi Mimarlık. Mermer görünümlü porselen, 60x120 ve 120x120 granit karo. Kayapınar showroom — online teklif.",
    keywords: localKeywords(
      'qua granite diyarbakır',
      'granit karo diyarbakır',
      'porselen karo 120x120 diyarbakır',
      'mermer görünümlü karo diyarbakır',
      'qua bayi diyarbakır'
    ),
    canonical: 'https://mayimimarlik.com/qua',
  });

  const schema = useMemo(() => {
    const catalog = buildBrandCatalogSchema({
      brandName: 'QUA Granite',
      description: 'QUA Granite porselen karo — Diyarbakır ana bayi kataloğu',
      url: 'https://mayimimarlik.com/qua',
      productCount: items.length,
    });
    const breadcrumb = buildBreadcrumbSchema([
      { name: 'Ana Sayfa', url: 'https://mayimimarlik.com/' },
      { name: 'QUA Granite', url: 'https://mayimimarlik.com/qua' },
    ]);
    const { '@context': _a, ...cat } = catalog;
    const { '@context': _b, ...crumb } = breadcrumb;
    return { '@context': 'https://schema.org', '@graph': [cat, crumb] };
  }, [items.length]);

  useStructuredData('schema-qua', schema);

  return <BrandCatalog config={quaBrand} items={items} />;
}
